import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSyncAlt, faServer, faExchangeAlt,
  faBook, faPercent, faBookOpen, faBuilding, faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import Layout from '../../components/Layout';
import Footer from '../../components/ui/Footer';
import HMSPageHeader from '../../components/ui/HMSPageHeader';
import HMSSkeleton from '../../components/ui/HMSSkeleton';
import HMSEmptyState from '../../components/ui/HMSEmptyState';
import abacusService from '../../services/abacusService';
import AbacusSyncStatusTab from './AbacusSyncStatusTab';
import AbacusAccountsTab from './AbacusAccountsTab';
import AbacusTaxCodesTab from './AbacusTaxCodesTab';
import AbacusGLJournalTab from './AbacusGLJournalTab';
import { useFlashMessage } from '../../components/FlashMessage';
import { extractApiError } from '../../utils/errorMessages';
import './OutgoingInvoice.css';
import '../../components/Abacus/AbacusSync.css';

// The four tabs ride the shared page header's pill tray (NavPillTabs items).
const TABS = [
  { key: 'sync', label: 'Documents', icon: faExchangeAlt },
  { key: 'accounts', label: 'Accounts', icon: faBook },
  { key: 'taxcodes', label: 'VAT codes', icon: faPercent },
  { key: 'gl', label: 'Ledger', icon: faBookOpen },
];

const AbacusSyncPage = () => {
  const history = useHistory();
  const [activeTab, setActiveTab] = useState('sync');
  const [status, setStatus] = useState(null);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [books, setBooks] = useState('');
  const [loadError, setLoadError] = useState('');
  const { showSuccess, showError } = useFlashMessage();

  // The list and the connection badge load separately: a failing connection
  // check must never hide the list, and a list that fails to load says so
  // instead of looking like "nothing here" (fresh-eyes review 2026-10-21).
  const refresh = useCallback(async () => {
    setLoading(true);
    const [statusResult, healthResult] = await Promise.allSettled([
      abacusService.getSyncStatus(),
      abacusService.healthCheck(),
    ]);
    if (statusResult.status === 'fulfilled') {
      const statusData = statusResult.value;
      setStatus(statusData);
      setLoadError('');
      const companies = statusData.companies || [];
      setBooks((current) => (companies.some((c) => c.books === current) ? current : (companies[0]?.books || '')));
    } else {
      setStatus(null);
      setLoadError(extractApiError(statusResult.reason, "The Abacus list couldn't be loaded. Press Refresh to try again."));
    }
    setHealth(healthResult.status === 'fulfilled'
      ? healthResult.value
      : { status: 'unhealthy', detail: extractApiError(healthResult.reason, "IMT couldn't check the Abacus connection.") });
    setLoading(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const renderTab = () => {
    if (loadError) {
      return (
        <div className="abacus-state" role="alert">
          <HMSEmptyState
            variant="card"
            icon={<FontAwesomeIcon icon={faExclamationTriangle} />}
            title={loadError}
          />
        </div>
      );
    }
    switch (activeTab) {
      case 'sync':
        return (
          <AbacusSyncStatusTab status={status} loading={loading} refresh={refresh}
            showSuccess={showSuccess} showError={showError} />
        );
      case 'accounts':
      case 'taxcodes':
      case 'gl': {
        if (!books) {
          return (
            <HMSEmptyState
              variant="card"
              className="abacus-state"
              icon={<FontAwesomeIcon icon={faBuilding} />}
              title="No IMT company sends to Abacus yet."
              hint="Add its Abacus books number under Corporate Structure."
            />
          );
        }
        if (activeTab === 'accounts') return <AbacusAccountsTab key={books} books={books} />;
        if (activeTab === 'taxcodes') return <AbacusTaxCodesTab key={books} books={books} />;
        return <AbacusGLJournalTab key={books} books={books} />;
      }
      default:
        return null;
    }
  };

  return (
    <Layout showLeftNavSidebar={true} showRightSidebar={false}>
      <div className="invoice-page-container master-data-screen abacus-erp-screen">
        <HMSPageHeader
          onBack={() => history.push('/invoicing')}
          backLabel="Back to Invoicing Dashboard"
          kicker="Finance"
          title="Abacus ERP"
          subtitle="Collected invoices go to each company's Abacus books by themselves"
          tabs={{ items: TABS, activeKey: activeTab, onChange: setActiveTab }}
          actions={(
            <>
              {health && (
                <span className={`status-badge abacus-health-badge ${health.status === 'healthy' ? 'approved' : 'rejected'}`}
                  title={health.detail || ''}>
                  <FontAwesomeIcon icon={faServer} />
                  {health.status === 'healthy' ? 'Connected' : health.status === 'unconfigured' ? 'Not set up' : 'Disconnected'}
                </span>
              )}
              <button type="button" className="btn-secondary" onClick={refresh} disabled={loading}>
                <FontAwesomeIcon icon={faSyncAlt} spin={loading} /> Refresh
              </button>
            </>
          )}
        />

        {activeTab !== 'sync' && (status?.companies || []).length > 1 && (
          <div className="search-filter-controls abacus-books-picker">
            <label htmlFor="abacus-books-picker">Company</label>
            <select id="abacus-books-picker" className="abacus-filter-input" value={books} onChange={(e) => setBooks(e.target.value)}>
              {status.companies.map((c) => (
                <option key={c.id} value={c.books}>{c.name} (books {c.books})</option>
              ))}
            </select>
          </div>
        )}

        {/* Active Tab Content */}
        <div className="abacus-tab-content">
          {loading && !status && activeTab === 'sync' ? (
            /* Skeleton in the shape of the document list that is coming. */
            <div className="abacus-state abacus-loading">
              <HMSSkeleton shape="title" />
              <HMSSkeleton lines={6} />
            </div>
          ) : (
            renderTab()
          )}
        </div>

        <Footer />
      </div>
    </Layout>
  );
};

export default AbacusSyncPage;
